// === INPACT:SLUITSTRAAT:FRISIA:APP:v2 ===
// Using @sluitstraat/core for shared components

import React, { useEffect, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';
import {
  ConfigProvider,
  useConfig,
  WorkflowProvider,
  ThemeProvider,
  ErrorBoundary,
  LoadingScreen,
  NavigationProgressProvider,
  setWorkflowConfig,
} from '@sluitstraat/core';
import Router from './Router';
import { frisiaWorkflowConfig } from './workflowConfig';

// Static logo available before config loads, so the initial loading screen
// shows the correct Frisia branding instead of the default placeholder.
const FRISIA_LOGO = '/logo.png';

// Initialize customer-specific workflow configuration at app startup
setWorkflowConfig(frisiaWorkflowConfig);

const AppContent: React.FC = () => {
  const { t } = useTranslation();
  const { config, isLoading, error } = useConfig();

  useEffect(() => {
    if (!isLoading && !error) {
      // Apply browser title from i18n, fallback to texts config, then branding
      document.title = t('common.browserTitle', { defaultValue: '' }) || config.texts?.general?.browserTitle || config.branding.appName;

      // Apply CSS variables for theming
      document.documentElement.style.setProperty('--primary-color', config.branding.primaryColor);
      document.documentElement.style.setProperty('--secondary-color', config.branding.secondaryColor);
      document.documentElement.style.setProperty('--accent-color', config.branding.accentColor || '#8ACDFF');
      document.documentElement.style.setProperty('--background-color', config.branding.backgroundColor || '#ffffff');
      document.documentElement.style.setProperty('--text-color', config.branding.textColor || '#1f2937');
      document.documentElement.style.setProperty('--foreground', config.branding.textColor || '#1f2937');
      document.documentElement.style.setProperty('--font-family', config.branding.fontFamily || 'GeistSans, system-ui, -apple-system, sans-serif');
      document.documentElement.style.setProperty('--font-geist-sans', config.branding.fontFamily || 'GeistSans, system-ui, -apple-system, sans-serif');

      // Apply custom CSS if provided
      if (config.branding.customCSS) {
        const styleElement = document.getElementById('custom-css-from-config');
        if (styleElement) {
          styleElement.textContent = config.branding.customCSS;
        } else {
          const newStyleElement = document.createElement('style');
          newStyleElement.id = 'custom-css-from-config';
          newStyleElement.textContent = config.branding.customCSS;
          document.head.appendChild(newStyleElement);
        }
      }

      // Apply select dropdown option styling
      const selectOptionsStyle = document.getElementById('select-options-styling');
      if (!selectOptionsStyle) {
        const newStyleElement = document.createElement('style');
        newStyleElement.id = 'select-options-styling';
        newStyleElement.textContent = `
          select option {
            padding: 12px 16px;
            background-color: #fff;
            color: #333;
            font-size: 16px;
          }
          select option:hover {
            background-color: var(--primary-color, #1976d2);
            color: #fff;
          }
          select option:checked {
            background-color: var(--primary-color, #1976d2);
            color: #fff;
            font-weight: 600;
          }
        `;
        document.head.appendChild(newStyleElement);
      }
    }
  }, [config, isLoading, error, t]);

  if (isLoading) {
    return <LoadingScreen message={t('messages.configLoading')} logo={FRISIA_LOGO} />;
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h1 style={{ color: '#d32f2f' }}>{t('errors.configError')}</h1>
        <p>{t('errors.configErrorDescription')}</p>
        <p style={{ color: '#666' }}>{error.message}</p>
      </div>
    );
  }

  return (
    <Suspense fallback={<LoadingScreen message={t('messages.configLoading')} logo={FRISIA_LOGO} />}>
      <NavigationProgressProvider>
        <WorkflowProvider persistToSessionStorage={false}>
          <Router />
        </WorkflowProvider>
      </NavigationProgressProvider>
    </Suspense>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}>
        <ThemeProvider>
          <ConfigProvider>
            <AppContent />
          </ConfigProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
